import { matchRepository } from "../storage/storage";
import Match from "../sports/tennis/match";
import GameService from "./game-service";
import ContextService from "./context-service";
import EventService from "./event-service";
import TennisSerializer from "./serializers/TennisSerializer";
import TennisRuleSetProcessor from "./ruleset-processors/tennis-ruleset-processor";
import TennisService from "./tennis-service";
import GameType from "../sports/tennis/enums/game-type";
import { getByCode } from "../sports/common/utils/enum-utils";
import { getMatchStats } from "../sports/tennis/utils/tennis-utils";
import { isDefined, isNumber } from "./common/object-util";
import ValidationError from "./exceptions/validation-error";
import TennisBoxscoreService from "./boxscores/tennis-boxscore-service";

class MatchService {
    async _orderFinalizedMatches(eventId, matches) {
        const game = await GameService.getGameByEventId(eventId);
        let gameContext = await ContextService.getGameContext(game.id);

        if (gameContext) {
            const finalizedMatches = matches.filter(
                (match) =>
                    match.hasNoPlayers() ||
                    match.isIncompleteFinish() ||
                    match.winnerId
            );
            gameContext.orderFinalizedMatches(finalizedMatches);
            gameContext = await ContextService.updateContext(gameContext);
        }

        return TennisSerializer.serializeGameContext(gameContext);
    }

    validateMatch(match) {
        match.sets.forEach((set) => {
            const { away, home } = set;
            if (
                (isDefined(away.score) && !isNumber(away.score)) ||
                (isDefined(away.tiebreak) && !isNumber(away.tiebreak)) ||
                (isDefined(home.score) && !isNumber(home.score)) ||
                (isDefined(home.tiebreak) && !isNumber(home.tiebreak))
            ) {
                throw new ValidationError(
                    `"Some set values are not numbers:" ${JSON.stringify(
                        set,
                        null,
                        2
                    )}`
                );
            }
        });
    }

    async saveMatch(match) {
        this.validateMatch(match);

        const game = await GameService.getGameByEventId(match.eventId);
        const currentMatches = await matchRepository
            .where({ eventId: match.eventId })
            .toArray();
        if (currentMatches.length === 0) {
            await TennisService.startGame(match.eventId);
        }
        await matchRepository.put(
            new Match({
                ...match,
                gameId: game.id,
                matchNumber:
                    match.matchNumber ||
                    currentMatches.filter((m) => m.isSingle === match.isSingle)
                        .length + 1
            })
        );

        await this._processRules(match.eventId);

        let { matches, totals } = await this.getMatches(match.eventId);
        return {
            matches,
            totals,
            gameContext: await this._orderFinalizedMatches(
                match.eventId,
                matches
            )
        };
    }

    async _reduceMatchesTotals(matches, gameType) {
        const setsToPlay = getByCode(GameType, gameType).value;
        return matches.reduce(
            (previousValue, match) => {
                return {
                    sets:
                        previousValue.sets +
                        match.home.setsWon +
                        match.away.setsWon,
                    gamesPerSet: [...Array(setsToPlay).keys()].map((i) => {
                        const { away, home } = match.sets[i] || {};
                        return (
                            previousValue.gamesPerSet[i] +
                            Number(home?.score) +
                            Number(away?.score)
                        );
                    })
                };
            },
            {
                sets: 0,
                gamesPerSet: [...Array(setsToPlay).keys()].map(() => 0)
            }
        );
    }

    async _processRules(eventId) {
        const { gameConfiguration } =
            await EventService.getGameConfigurationOrDefault(eventId);
        const matches = await matchRepository
            .where({ eventId: eventId })
            .toArray();
        const game = await GameService.getGameByEventId(eventId);
        const gameContext = await ContextService.getGameContext(game.id);

        await TennisRuleSetProcessor.process(
            gameContext,
            gameConfiguration,
            {
                matches
            },
            {}
        );
        await ContextService.updateContext(gameContext);
        await matchRepository.bulkPut(matches);
    }

    async getMatches(eventId) {
        const { gameConfiguration } =
            await EventService.getGameConfigurationOrDefault(eventId);

        let matches = await matchRepository
            .where({ eventId: eventId })
            .toArray();
        matches = matches.map((match) => {
            return Object.assign(match, {
                stats: getMatchStats(match, gameConfiguration)
            });
        });

        const finalizedMatches = matches.filter(
            (match) =>
                match.hasNoPlayers() ||
                match.isIncompleteFinish() ||
                match.winnerId
        );
        const singlesTotals = await this._reduceMatchesTotals(
            finalizedMatches.filter((match) => match.isSingle),
            gameConfiguration.singles?.gameType
        );
        const doublesTotals = await this._reduceMatchesTotals(
            finalizedMatches.filter((match) => !match.isSingle),
            gameConfiguration.doubles?.gameType
        );

        return {
            matches,
            totals: { singles: singlesTotals, doubles: doublesTotals }
        };
    }

    async getMatch(id) {
        return matchRepository.getById(id);
    }

    async deleteMatch(id) {
        const { eventId, isSingle } = await this.getMatch(id);
        await matchRepository.delete(id);

        const currentMatches = await matchRepository
            .where({ eventId })
            .toArray();

        if (currentMatches.length === 0) {
            const game = await GameService.getGameByEventId(eventId);
            const gameContext = await ContextService.getGameContext(game.id);
            gameContext.gameInitialized = false;
            await ContextService.updateContext(gameContext);
        }

        const filteredMatches = currentMatches.filter(
            (m) => m.isSingle === isSingle
        );
        filteredMatches.forEach((m, i) => (m.matchNumber = i + 1));
        await matchRepository.bulkPut(filteredMatches);

        await this._processRules(eventId);

        let { matches, totals } = await this.getMatches(eventId);
        return {
            matches,
            totals,
            gameContext: await this._orderFinalizedMatches(eventId, matches)
        };
    }
}

export default new MatchService();
